<template>
  <div class="w-full p-6 bg-white rounded-lg shadow">
    <h4 class="text-base font-semibold">{{ title }}</h4>
    <p class="text-sm text-grey">{{ description }}</p>
    <div class="mt-4">
      <button class="sk-btn sk-btn-primary btn-border rounded-lg" @click="handleCheck">
        {{ buttonLabel }}
      </button>
    </div>
    <SKModal ref="checkDataModal" :title="modalTitle" closeButtonKey="apps.modals.cancel" actionButtonsPosition="center">
      <template #body>
        <div class="min-w-[320px]">
          <div v-if="modalWarnings.length" class="mb-4">
            <div v-for="(warning, index) in modalWarnings" :key="index" class="text-danger text-sm">
              {{ warning }}
            </div>
          </div>

          <div v-for="(section, index) in modalSections" :key="index" class="mb-4">
            <h5 class="text-sm font-semibold mb-2">{{ section.title }}</h5>
            <div v-if="section.rows.length === 0" class="text-sm text-grey">{{ $t('widgets.modal.no_data') }}</div>
            <div
              v-for="(row, rowIndex) in section.rows"
              :key="rowIndex"
              class="flex items-center justify-between py-1"
            >
              <div class="flex items-center gap-2">
                <img v-if="row.icon" :src="row.icon" class="w-5 h-5" />
                <span class="text-sm">{{ row.label }}</span>
              </div>
              <span class="text-sm font-medium">{{ row.value }}</span>
            </div>
          </div>

          <div v-if="modalNotes.length" class="text-xs text-grey">
            <div v-for="(note, index) in modalNotes" :key="index">{{ note }}</div>
          </div>
        </div>
      </template>
    </SKModal>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import WeatherForecastService from '@/modules/Apps/Services/WeatherForecastService'
import loader from '@/modules/Shared/Mixins/loader'
import SKModal from '@/modules/Shared/Components/modal/SKModal.vue'

export default {
  name: 'CheckWeatherPanel',
  mixins: [loader],
  components: {
    SKModal
  },
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    buttonLabel: {
      type: String,
      required: true
    },
    panelType: {
      type: String,
      required: true
    },
    selectedResortUuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      modalTitle: '',
      modalWarnings: [],
      modalNotes: [],
      modalSections: []
    }
  },
  methods: {
    setModalNotes(notes) {
      this.modalNotes = notes
    },
    openModal(title, warnings, sections) {
      this.modalTitle = title
      this.modalWarnings = warnings
      this.modalSections = sections
      if (!this.modalNotes) {
        this.modalNotes = []
      }
      this.$refs.checkDataModal.open()
    },
    async handleCheck() {
      await this.checkWeatherData()
    },
    async checkWeatherData() {
      this.setModalNotes([])
      const success = await this.loadAndIgnoreError(async () => {
        const forecast = await WeatherForecastService.getWeatherForecast(this.selectedResortUuid)
        const warnings = []

        if (!forecast) {
          warnings.push(this.$t('widgets.modal.warning.no_data'))
        }

        const updatedAt = this.getWeatherUpdatedAt(forecast)
        if (this.isOutdated(updatedAt, 2)) {
          warnings.push(this.$t('widgets.modal.warning.outdated_2'))
        }

        const rows = this.buildWeatherRows(forecast)
        if (rows.length === 0) {
          warnings.push(this.$t('widgets.modal.warning.no_data_shown'))
        }

        this.openModal(this.$t('widgets.modal.weather.title'), warnings, [{ title: this.$t('widgets.modal.today.title'), rows }])
      })

      if (!success) {
        this.notifyError(this.$t('shops.error.0'), this.$t('widgets.notifications.fetch_weather_error'))
      }
    },
    getWeatherUpdatedAt(forecast) {
      if (!forecast) {
        return null
      }
      if (forecast.updated_timestamp) {
        return forecast.updated_timestamp
      }
      if (forecast.updated) {
        return typeof forecast.updated === 'number'
          ? forecast.updated
          : DateTime.fromISO(forecast.updated).toSeconds()
      }
      const days = forecast.days || []
      if (days.length && days[0].updated) {
        return typeof days[0].updated === 'number'
          ? days[0].updated
          : DateTime.fromISO(days[0].updated).toSeconds()
      }
      return null
    },
    buildWeatherRows(forecast) {
      if (!forecast) {
        return []
      }
      const days = forecast.days || []
      const today = days[0]
      if (!today) {
        return []
      }
      const zones = today.zones || today.zone || today.weather_zones || []
      const zone = Array.isArray(zones) ? zones[0] : zones
      if (!zone) {
        return []
      }
      const times = zone.times || zone.times_of_day || zone.day_parts || zone.forecast || []
      const time = Array.isArray(times) ? times[0] : times
      if (!time) {
        return []
      }

      const maxTemp = this.pickFirstValue(time, ['temp_max', 'max_temperature', 'temperature_max', 'max_temp'])
      const minTemp = this.pickFirstValue(time, ['temp_min', 'min_temperature', 'temperature_min', 'min_temp'])
      const temp = this.pickFirstValue(time, ['temperature', 'temp'])
      const symbol = this.pickFirstValue(time, ['symbol', 'weather_symbol', 'icon', 'symbol_code', 'weather_icon'])

      const rows = []
      if (maxTemp != null && minTemp != null && symbol != null) {
        rows.push({ label: this.$t('widgets.modal.labels.max_temp'), value: String(maxTemp) })
        rows.push({ label: this.$t('widgets.modal.labels.min_temp'), value: String(minTemp) })
        rows.push({ label: this.$t('widgets.modal.labels.symbol'), value: String(symbol) })
        return rows
      }

      if (temp != null && symbol != null) {
        rows.push({ label: this.$t('widgets.modal.labels.temperature'), value: String(temp) })
        rows.push({ label: this.$t('widgets.modal.labels.symbol'), value: String(symbol) })
        return rows
      }

      if (symbol != null) {
        rows.push({ label: this.$t('widgets.modal.labels.symbol'), value: String(symbol) })
        return rows
      }

      return []
    },
    isOutdated(timestampSeconds, maxDays) {
      if (!timestampSeconds) {
        return false
      }
      const timestamp = DateTime.fromSeconds(timestampSeconds)
      return DateTime.now().diff(timestamp, 'days').days > maxDays
    },
    pickFirstValue(source, keys) {
      if (!source) {
        return null
      }
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          return source[key]
        }
      }
      return null
    }
  }
}
</script>

